import { decodeHTML } from 'entities';
import { get, includes, isEmpty, isUndefined } from 'lodash';
import { resolve, reject } from 'redux-optimist-promise';
import { getTimestamp } from 'Utils/date/dateUtils';
import {
  PRODUCT_FACET_ID,
  DEPARTURE_DATE_MAX_FACET_ID,
  LOCALE_FACET_ID,
  MAX_NUMBER_OF_SELECTED_PRODUCTS,
  DEPARTURE_CITY_FACET_ID,
  ACTIVITIES_FACET_ID,
  CHILDCARE_CRITERIA_FACET_ID,
  CHILDCARE_FILTER_FACET_ID,
  COUNTRIES_FACET_ID,
  DESTINATIONS_FACET_ID,
  FILTERABLE_FACETS_IDS,
  FLIGHT_DURATION_FACET_AS_LIST,
  TYPES_OF_STAY_FACET_ID,
  DURATION_FACET,
  QUERY_QUERY_PARAM,
  SEASON_FACET_ID,
  SORT_BY_PRICE_QUERY_PARAM,
  TOP_COUNTRIES_FACET_LIST_ID,
  CUSTOM_FACETS_IDS,
} from 'Utils/faceting/constants';
import {
  customFacetsQueryParamMapping,
  facetsQueryParamMapping,
  getActiveFacetsFromQueryParams,
} from 'Utils/faceting/facetingUtils';
import { SYNC_WITH_LOCATION } from 'Store/constants';
import createReducer from 'Store/utils/createReducer';
import type { ValidationState } from 'Store/trip-information/kids/constants';
import 'Store/trip-information/kids/constants';
import {
  FACETING_CHILDCARE_REQUEST,
  FACETING_CLEAR_FACETS,
  FACETING_CLEAR_ALL,
  FACETING_LOAD_OFFSET_SELL_REQUEST,
  FACETING_LOAD_PRODUCTS_REQUEST,
  FACETING_PRICES_REQUEST,
  FACETING_RESET_CUSTOM_FILTERS,
  FACETING_RESET_FILTERS,
  FACETING_RESET_REDIRECTION,
  FACETING_RESET_SEARCH,
  FACETING_SEARCH_REQUEST,
  FACETING_SET_PRODUCTS_FILTERED_WITH_ACTIVE_CUSTOM_FACETS,
  FACETING_SET_CUSTOM_FILTERS,
  FACETING_SET_FILTERS,
  FACETING_SET_SEARCH_FILTERS,
  FACETING_SET_INITIAL_PRICES,
  FACETING_SET_IS_DATE_REQUIRED,
  FACETING_SET_IS_SEARCH_ENABLED,
  FACETING_SET_ORDER_BY_PRICE,
  FACETING_SET_SEARCH_QUERY,
  FACETING_SET_KIDS_VALIDATION_STATE,
  FACETING_SET_PRODUCT_COMPARISON_SELECTION_MODE,
  FACETING_TOGGLE_PRODUCT_SELECTED_FOR_COMPARISON,
  FACETING_TOGGLE_FACET_SELECTED,
  FACETING_UPDATE_CUSTOM_FILTERS,
  FACETING_UPDATE_FILTERS,
  FACETING_SET_PRODUCTS_BEFORE_ACTIVE_CUSTOM_FACETS_FILTERING,
} from './constants';
const IGNORED_FILTERS_IDS = [
  PRODUCT_FACET_ID,
  DEPARTURE_DATE_MAX_FACET_ID,
  LOCALE_FACET_ID,
  COUNTRIES_FACET_ID,
];

function catalogFacetItemToState(item: PertimmFacet): SearchFacetList {
  return {
    label: item.i18n,
    value: item.filter,
    count: item.freq,
  };
}

// Pertimm will always send `interpretation_explanation.interpretations` dates
// in the format 'DD/MM/YYYY', for any locale
const PERTIMM_INTERPRETATION_DATE_FORMAT = 'DD/MM/YYYY';
const PERTIMM_DEPARTURE_MIN_DATE = 'departure_date_min';
const PERTIMM_DEPARTURE_MAX_DATE = 'departure_date_max';

/**
 * Takes the Pertimm catalog of facets and restructure it before storage in redux store.
 */
function catalogToState(
  catalogs: Array<PertimmFacetCategory> = [],
  activeFacets: any,
  completeCountriesFacetAsTree: SearchFacetCategory,
  facetingTopCountries: CMSFacetingTopCountries,
): Array<SearchFacetCategory> {
  const catalogIdsAsList = [
    CHILDCARE_CRITERIA_FACET_ID,
    CHILDCARE_FILTER_FACET_ID,
    COUNTRIES_FACET_ID,
    DEPARTURE_CITY_FACET_ID,
    DURATION_FACET,
    SEASON_FACET_ID,
    TYPES_OF_STAY_FACET_ID,
  ];
  const catalogIdsAsTree = [ACTIVITIES_FACET_ID, DESTINATIONS_FACET_ID];
  const facetsAsList = catalogs
    .filter((catalog) => catalogIdsAsList.includes(catalog.id))
    .map(({ id, name, list }) => ({
      id,
      name,
      list: list.map(catalogFacetItemToState),
    }));
  facetsAsList.push(FLIGHT_DURATION_FACET_AS_LIST);
  const facetsAsTree = catalogs
    .filter((catalog) => catalogIdsAsTree.includes(catalog.id))
    .map(({ id, name, tree }) => ({
      id,
      name,
      list: tree.map((list) => ({
        id: list.name,
        ...catalogFacetItemToState(list),
        list: list.tree.map(catalogFacetItemToState),
      })),
    }));
  const [countriesFacetAsList] = facetsAsList.filter(({ id }) => id === COUNTRIES_FACET_ID);
  const [destinationsFacetAsTree] = facetsAsTree.filter(({ id }) => id === DESTINATIONS_FACET_ID);
  const topCountriesFacetAsList = getTopCountriesFacetList(
    facetingTopCountries,
    countriesFacetAsList,
  );
  const countriesFacetAsTree =
    countriesFacetAsList && destinationsFacetAsTree
      ? getCountriesFacetAsTree(
          activeFacets,
          completeCountriesFacetAsTree,
          countriesFacetAsList,
          destinationsFacetAsTree,
          topCountriesFacetAsList,
        )
      : null;
  return [
    ...facetsAsList
      .map((facet) => (facet.id === COUNTRIES_FACET_ID ? countriesFacetAsTree : facet))
      .filter(Boolean),
    ...facetsAsTree,
  ];
}

function filtersToActiveFacetsState(filters: Array<PertimmActiveFacet> = []) {
  return filters.reduce((acc, filter) => {
    const categoryId = filter.id;

    if (FILTERABLE_FACETS_IDS.includes(categoryId)) {
      return { ...acc, [categoryId]: { ...acc[categoryId], [filter.value]: true } };
    }

    return acc;
  }, {});
}

function sanitizeFilters(filters: Array<PertimmActiveFacet>): Array<any> {
  return filters.filter((filter) => !includes(IGNORED_FILTERS_IDS, get(filter, 'id')));
}

function sanitizeLabel(label: string): string {
  return label ? label.toLowerCase().replace(/\s+/g, '') : '';
}

function sanitizeValue(value: string): string {
  return value ? value.toUpperCase().replace(/\s+/g, '') : '';
}

function removeDuplicateCountries(countryList: Array<SearchFacetList>): Array<SearchFacetList> {
  const countryLabels = {};
  return countryList.reduce((result, country) => {
    const label = sanitizeLabel(country.label);

    if (!countryLabels[label]) {
      countryLabels[label] = { ...country };
      result.push(countryLabels[label]);
    }

    return result;
  }, []);
}

function trimLabel(label: string): string {
  return label ? label.trim() : '';
}

function filtersToState(filters: Array<PertimmActiveFacet> = []): Array<any> {
  return sanitizeFilters(filters).map((filter) => ({
    id: get(filter, 'id'),
    name: get(filter, 'value'),
    label: get(filter, 'value_i18n'),
    selected: true,
  }));
}

function getCountriesFacetAsTree(
  activeFacets,
  completeCountriesFacetAsTree: SearchFacetCategory,
  countriesFacetAsList: SearchFacetCategory,
  destinationsFacetAsTree: SearchFacetCategory,
  topCountriesFacetAsList?: SearchFacetList | null,
): SearchFacetCategory {
  const destinationsActiveFacets = activeFacets[DESTINATIONS_FACET_ID];
  const initialValueForCountriesFacetAsTree =
    completeCountriesFacetAsTree &&
    completeCountriesFacetAsTree.list.length >= destinationsFacetAsTree.list.length
      ? completeCountriesFacetAsTree
      : { ...destinationsFacetAsTree, id: COUNTRIES_FACET_ID };
  return {
    ...initialValueForCountriesFacetAsTree,
    list: [
      topCountriesFacetAsList,
      ...initialValueForCountriesFacetAsTree.list
        .map((region) => {
          const { label: regionLabel, value } = region;
          // Retrieve updated data for the current region in destinationsFacetAsTree
          const destinationsFacetRegion = destinationsFacetAsTree?.list?.find(
            ({ label: regionLabelFromTree }) =>
              sanitizeLabel(regionLabel) === sanitizeLabel(regionLabelFromTree),
          );

          // Check if there are region filters selected, if there are but the current region is not in the active filters, remove it
          if (destinationsActiveFacets && !destinationsActiveFacets[value]) {
            return null;
          }

          // Retrieve stored data for the current region in completeCountriesFacetRegion
          const completeCountriesFacetRegion = completeCountriesFacetAsTree?.list?.find(
            ({ label: regionLabelFromTree }) =>
              sanitizeLabel(regionLabel) === sanitizeLabel(regionLabelFromTree),
          );
          const completeCountriesFacetRegionList = completeCountriesFacetRegion?.list || [];
          const destinationsFacetRegionList = destinationsFacetRegion?.list || [];
          // Keep stored data if it's country list is bigger than the updated data
          const list =
            completeCountriesFacetRegionList.length > destinationsFacetRegionList.length
              ? completeCountriesFacetRegionList
              : destinationsFacetRegionList;
          return {
            ...region,
            label: trimLabel(regionLabel),
            list: removeDuplicateCountries(
              list
                .map((country) => {
                  const { label: countryLabel } = country;
                  // Retrieve updated data for the current country in countriesFacetAsList
                  const countriesFacetCountry = countriesFacetAsList.list.find(
                    ({ label: countryLabelFromList }) =>
                      sanitizeLabel(countryLabel) === sanitizeLabel(countryLabelFromList),
                  );
                  // Update current country's data if it is in countriesFacetAsList, otherwise remove it
                  return countriesFacetCountry
                    ? {
                        ...country,
                        count: countriesFacetCountry.count,
                        label: trimLabel(countriesFacetCountry.label),
                        value: countriesFacetCountry.value,
                      }
                    : null;
                })
                .filter(Boolean),
            ),
          };
        })
        .filter(Boolean)
        .filter(({ id }) => id !== TOP_COUNTRIES_FACET_LIST_ID),
    ]
      .filter(Boolean)
      .filter(({ list }) => list && list.length > 0),
  };
}

function getTopCountriesFacetList(
  facetingTopCountries: CMSFacetingTopCountries,
  countriesFacetAsList: SearchFacetCategory,
): SearchFacetList | null {
  const topCountries = facetingTopCountries.enabled
    ? facetingTopCountries.items.map(({ id }) => id)
    : [];
  const topCountriesList =
    countriesFacetAsList && countriesFacetAsList.list
      ? topCountries
          .map((countryCode) => {
            return countriesFacetAsList.list.find(
              ({ value }) => sanitizeValue(value) === countryCode,
            );
          })
          .filter(Boolean)
      : [];
  return topCountriesList.length > 0
    ? {
        count: topCountriesList
          .reduce(
            (result, country) =>
              country && country.count ? result + parseInt(country.count, 10) : result,
            0,
          )
          .toString(),
        id: TOP_COUNTRIES_FACET_LIST_ID,
        label: trimLabel(facetingTopCountries.title),
        list: removeDuplicateCountries(
          topCountriesList.map((country) => {
            return { ...country, label: trimLabel(country.label) };
          }),
        ),
        value: facetingTopCountries.title,
      }
    : null;
}

function getTotalLabelsCount(facet: SearchFacetCategory) {
  return facet.list.reduce(
    (result, facetList) => (facetList && facetList.list ? result + facetList.list.length : result),
    0,
  );
}

function parseBanners(banners = []) {
  try {
    return banners.map((banner) => ({ ...banner, content: JSON.parse(banner.content) }));
  } catch (e) {
    return [];
  }
}

function removeUnselectedFiltersFromActiveFacetsState(
  filters: Array<PertimmActiveFacet> = [],
  activeFacetsState: Record<string, Record<string, Record<string, boolean>>>,
) {
  const activeFacets = { ...activeFacetsState };
  const newActiveFacets = {};

  if (filters.length) {
    for (const categoryId in activeFacets) {
      if (activeFacets.hasOwnProperty(categoryId)) {
        const categoryIdFilter = [...filters].filter(({ id }) => id === categoryId);

        if (categoryIdFilter.length) {
          newActiveFacets[categoryId] = {};

          for (const categoryName in activeFacets[categoryId]) {
            if (
              activeFacets[categoryId].hasOwnProperty(categoryName) && // @ts-expect-error TODO(flow-migration)
              categoryIdFilter.some(({ name, id }) => name === categoryName && id === categoryId)
            ) {
              newActiveFacets[categoryId][categoryName] = activeFacets[categoryId][categoryName];
            }
          }
        }
      }
    }
  }

  return newActiveFacets;
}

const initialState = {
  // Special pertimm marchandising rules with pre-parameterized criteria
  // such as `minDate` / `maxDate` for some user queries such as `noel`.
  pertimmInterpretations: {},
  products: [],
  recommandedProducts: [],
  facets: [],
  activeCustomFacets: {},
  activeFacets: {},
  childcareAvailabilities: [],
  customFilters: [],
  filters: [],
  completeCountriesFacetAsTree: null,
  query: {
    label: '',
  },
  redirection: null,
  scrollToResults: false,
  isLoading: true,
  isPriceLoading: true,
  isLoadingChildcareAvailabilities: false,
  isSearchEnabled: false,
  isSelectionModeEnabled: false,
  kidsValidationState: [],
  joyviewOffsetSell: -1,
  productsBeforeActiveCustomFacetsFiltering: null,
  productsFilteredWithActiveCustomFacets: null,
  resortOffsetSell: -1,
  orderByPrice: '',
  productsSelectedForComparison: [],
  prices: [],
  noResults: false,
  lastRequestId: -1,
  lastRecommandedRequestId: -1,
  isDateRequired: false,
  searchFilterList: [],
};
const facetingReducer = createReducer(initialState, {
  [FACETING_RESET_REDIRECTION](state: ReduxState) {
    return { ...state, redirection: null };
  },

  [FACETING_RESET_SEARCH](state: ReduxState) {
    return {
      ...state,
      products: [],
      productsBeforeActiveCustomFacetsFiltering: null,
      productsFilteredWithActiveCustomFacets: null,
      prices: [],
      childcareAvailabilities: [],
      redirection: null,
      scrollToResults: false,
      isLoading: true,
      isPriceLoading: true,
      noResults: false,
      searchIsEnabled: false,
    };
  },

  [FACETING_LOAD_OFFSET_SELL_REQUEST](state: ReduxState) {
    return { ...state, joyviewOffsetSell: -1, resortOffsetSell: -1 };
  },

  [resolve(FACETING_LOAD_OFFSET_SELL_REQUEST)](
    state: ReduxState,
    action: LoadOffsetSellRequestResolvedAction,
  ): ReduxState {
    const { locale } = action.payload;
    return {
      ...state,
      joyviewOffsetSell: locale?.joyviewOffsetSell || -1,
      resortOffsetSell: locale?.resortOffsetSell || -1,
    };
  },

  [reject(FACETING_LOAD_OFFSET_SELL_REQUEST)](state: ReduxState): ReduxState {
    return { ...state, joyviewOffsetSell: -1, resortOffsetSell: -1 };
  },

  [FACETING_LOAD_PRODUCTS_REQUEST](state: ReduxState, action: LoadProductsRequestResolvedAction) {
    return {
      ...state,
      childcareAvailabilities: [],
      isLoading: true,
      isPriceLoading: true,
      lastRecommandedRequestId: get(action, 'optimist.id', -1),
      recommandedProducts: [],
      noResults: true,
      prices: [],
    };
  },

  [resolve(FACETING_LOAD_PRODUCTS_REQUEST)](
    state: ReduxState,
    action: LoadProductsRequestResolvedAction,
  ): ReduxState {
    // If there is another request in progress
    if (action?.optimist?.id !== state.lastRecommandedRequestId) {
      return state;
    }

    const { resorts } = action?.payload ?? {};
    return { ...state, isLoading: false, recommandedProducts: resorts, noResults: true };
  },

  [reject(FACETING_LOAD_PRODUCTS_REQUEST)](state: ReduxState): ReduxState {
    return { ...state, isLoading: false, isPriceLoading: false, noResults: true };
  },

  [FACETING_SEARCH_REQUEST](state: ReduxState, action: FacetingSearchRequestResolvedAction) {
    return {
      ...state,
      childcareAvailabilities: [],
      isLoading: true,
      isPriceLoading: true,
      lastRequestId: get(action, 'optimist.id', -1),
      noResults: false,
      prices: [],
    };
  },

  [resolve(FACETING_SEARCH_REQUEST)](
    state: ReduxState,
    action: FacetingSearchRequestResolvedAction,
  ): ReduxState {
    // If there is another request in progress
    if (get(action, 'optimist.id') !== state.lastRequestId) {
      return state;
    }

    const { products } = action.payload.search;
    const productsSelectedForComparison = get(state, 'productsSelectedForComparison', []).filter(
      (selectedProductId) => products.some((product) => product.id === selectedProductId),
    );
    const pertimmInterpretations = get(
      action.payload,
      'interpretation_explanation.interpretations',
      [],
    ).reduce((acc, interpretation) => {
      if (interpretation.filter_name === PERTIMM_DEPARTURE_MIN_DATE) {
        acc.minDate = getTimestamp(interpretation.filter_value, PERTIMM_INTERPRETATION_DATE_FORMAT);
      }

      if (interpretation.filter_name === PERTIMM_DEPARTURE_MAX_DATE) {
        acc.maxDate = getTimestamp(interpretation.filter_value, PERTIMM_INTERPRETATION_DATE_FORMAT);
      }

      return acc;
    }, {});
    const activeFacets = filtersToActiveFacetsState(action.payload.filters);
    const { facetingTopCountries } = action.payload;
    const facets = !isEmpty(action.payload.catalogs)
      ? catalogToState(
          action.payload.catalogs,
          activeFacets,
          state.completeCountriesFacetAsTree,
          facetingTopCountries,
        )
      : state.facets || [];
    const [countriesFacetAsTree] = facets.filter((catalog) => COUNTRIES_FACET_ID === catalog.id);
    return {
      ...state,
      activeFacets,
      banners: parseBanners(action.payload.banners),
      // We need to store the complete country list because pertimm doesn't return all of them once a filter is selected
      completeCountriesFacetAsTree:
        !state.completeCountriesFacetAsTree ||
        (countriesFacetAsTree &&
          getTotalLabelsCount(countriesFacetAsTree) >
            getTotalLabelsCount(state.completeCountriesFacetAsTree))
          ? countriesFacetAsTree
          : state.completeCountriesFacetAsTree,
      // If the catalog of facets is empty (e.g. "no results"), keep the previous state
      facets,
      filters: filtersToState(action.payload.filters),
      isLoading: false,
      noResults: !action.payload.redirection && products.length === 0,
      pertimmInterpretations,
      products,
      productsBeforeActiveCustomFacetsFiltering: null,
      productsFilteredWithActiveCustomFacets: null,
      productsSelectedForComparison,
      query: { ...state.query, label: decodeHTML(action.payload.query) },
      redirection: action.payload.redirection,
    };
  },

  [reject(FACETING_SEARCH_REQUEST)](state: ReduxState): ReduxState {
    return { ...state, isLoading: false, isPriceLoading: false, noResults: true };
  },

  [FACETING_SET_PRODUCTS_FILTERED_WITH_ACTIVE_CUSTOM_FACETS](state: ReduxState, action: any) {
    return {
      ...state,
      productsFilteredWithActiveCustomFacets: action.payload.productsFilteredWithActiveCustomFacets,
    };
  },

  [FACETING_SET_PRODUCTS_BEFORE_ACTIVE_CUSTOM_FACETS_FILTERING](state: ReduxState, action: any) {
    return {
      ...state,
      productsBeforeActiveCustomFacetsFiltering:
        action.payload.productsBeforeActiveCustomFacetsFiltering,
    };
  },

  [FACETING_PRICES_REQUEST](state: ReduxState): ReduxState {
    return { ...state, isPriceLoading: true, prices: [] };
  },

  [resolve(FACETING_PRICES_REQUEST)](state: ReduxState, action: any): ReduxState {
    const { resorts } = action?.payload ?? {};
    return { ...state, isPriceLoading: false, prices: resorts, scrollToResults: false };
  },

  [reject(FACETING_PRICES_REQUEST)](state: ReduxState): ReduxState {
    return { ...state, isPriceLoading: false, prices: [], scrollToResults: true };
  },

  [FACETING_CHILDCARE_REQUEST](state: ReduxState): ReduxState {
    return { ...state, isLoadingChildcareAvailabilities: true, childcareAvailabilities: [] };
  },

  [resolve(FACETING_CHILDCARE_REQUEST)](state: ReduxState, action: any): ReduxState {
    const { childcareSearch } = action.payload;
    return {
      ...state,
      isLoadingChildcareAvailabilities: false,
      childcareAvailabilities: childcareSearch,
    };
  },

  [reject(FACETING_CHILDCARE_REQUEST)](state: ReduxState): ReduxState {
    return { ...state, isLoadingChildcareAvailabilities: false, childcareAvailabilities: [] };
  },

  [FACETING_SET_CUSTOM_FILTERS](state: ReduxState, action: SetCustomFiltersAction): ReduxState {
    return { ...state, customFilters: action.payload.customFilters };
  },

  [FACETING_SET_FILTERS](state: ReduxState, action: SetFiltersAction): ReduxState {
    return { ...state, filters: action.payload.filters };
  },

  [FACETING_SET_SEARCH_FILTERS](state: ReduxState, action: any): ReduxState {
    return { ...state, searchFilterList: action.payload.searchFilterList };
  },

  [FACETING_SET_INITIAL_PRICES](
    state: ReduxState,
    action: SetInitialProductPricesAction,
  ): ReduxState {
    return { ...state, prices: action.payload.initialProductPrices };
  },

  [FACETING_SET_IS_SEARCH_ENABLED](state: ReduxState, action): ReduxState {
    return { ...state, isSearchEnabled: action.payload.isSearchEnabled };
  },

  [FACETING_SET_KIDS_VALIDATION_STATE](
    state: ReduxState,
    action: Action<{
      kidsValidationState: Array<ValidationState>;
    }>,
  ): ReduxState {
    return { ...state, kidsValidationState: action.payload.kidsValidationState };
  },

  [FACETING_SET_PRODUCT_COMPARISON_SELECTION_MODE](state: ReduxState, action): ReduxState {
    return {
      ...state,
      isSelectionModeEnabled: action.payload.enable,
      productsSelectedForComparison: [],
    };
  },

  [FACETING_TOGGLE_PRODUCT_SELECTED_FOR_COMPARISON](state, action) {
    const productsCurrentlySelected = get(state, 'productsSelectedForComparison');
    const productToBeToggled = get(action, 'payload.productId', null);

    if (!productToBeToggled) {
      return state;
    }

    if (includes(productsCurrentlySelected, productToBeToggled)) {
      return {
        ...state,
        productsSelectedForComparison: productsCurrentlySelected.filter(
          (p) => p !== productToBeToggled,
        ),
      };
    }

    if (productsCurrentlySelected.length < MAX_NUMBER_OF_SELECTED_PRODUCTS) {
      return {
        ...state,
        productsSelectedForComparison: [...productsCurrentlySelected, productToBeToggled],
      };
    }

    return state;
  },

  [SYNC_WITH_LOCATION](state: ReduxState, action: Action<any>): ReduxState {
    const { payload: { location: { queryParams } } = {} } = action;
    const orderByPrice = ['asc', 'desc'].includes(queryParams[SORT_BY_PRICE_QUERY_PARAM])
      ? queryParams[SORT_BY_PRICE_QUERY_PARAM]
      : '';

    if (!queryParams[QUERY_QUERY_PARAM]) {
      return {
        ...state,
        activeCustomFacets: {
          ...state.activeCustomFacets,
          ...getActiveFacetsFromQueryParams(queryParams, customFacetsQueryParamMapping),
        },
        activeFacets: {
          ...state.activeFacets,
          ...getActiveFacetsFromQueryParams(queryParams, facetsQueryParamMapping),
        },
        orderByPrice,
      };
    }

    return {
      ...state,
      activeCustomFacets: {
        ...state.activeCustomFacets,
        ...getActiveFacetsFromQueryParams(queryParams, customFacetsQueryParamMapping),
      },
      activeFacets: {
        ...state.activeFacets,
        ...getActiveFacetsFromQueryParams(queryParams, facetsQueryParamMapping),
      },
      query: { ...state.query, label: queryParams[QUERY_QUERY_PARAM] },
      orderByPrice,
    };
  },

  [FACETING_SET_SEARCH_QUERY](state: ReduxState, { payload: { value } }): ReduxState {
    if (isUndefined(value)) {
      return state;
    }

    return { ...state, query: { ...value, label: decodeHTML(value.label) } };
  },

  [FACETING_CLEAR_FACETS](state: ReduxState, action): ReduxState {
    const { categoryId } = action.payload;

    if (CUSTOM_FACETS_IDS.includes(categoryId)) {
      return { ...state, activeCustomFacets: { ...state.activeCustomFacets, [categoryId]: {} } };
    }

    return { ...state, activeFacets: { ...state.activeFacets, [categoryId]: {} } };
  },

  [FACETING_CLEAR_ALL](state: ReduxState): ReduxState {
    return { ...state, activeCustomFacets: {}, activeFacets: {} };
  },

  [FACETING_TOGGLE_FACET_SELECTED](state: ReduxState, action): ReduxState {
    const { payload: facet } = action;
    const { categoryId } = facet;
    const isCustomFacetId = CUSTOM_FACETS_IDS.includes(categoryId);
    const categoryActiveFacets = isCustomFacetId
      ? state.activeCustomFacets[categoryId]
      : state.activeFacets[categoryId];
    let newState;

    if (facet.isSelected) {
      newState = { ...categoryActiveFacets, [facet.value]: facet.isSelected };
    } else {
      const { [facet.value]: _, ...rest } = categoryActiveFacets; // eslint-disable-line

      newState = rest;
    }

    if (isCustomFacetId) {
      return {
        ...state,
        activeCustomFacets: { ...state.activeCustomFacets, [categoryId]: { ...newState } },
      };
    }

    return { ...state, activeFacets: { ...state.activeFacets, [categoryId]: { ...newState } } };
  },

  [FACETING_SET_IS_DATE_REQUIRED](state: ReduxState, action): ReduxState {
    return { ...state, isDateRequired: action.payload.isDateRequired };
  },

  [FACETING_SET_ORDER_BY_PRICE](state: ReduxState, action): ReduxState {
    return { ...state, orderByPrice: action.payload.orderByPrice };
  },

  [FACETING_UPDATE_CUSTOM_FILTERS](state: ReduxState, action: SetCustomFiltersAction): ReduxState {
    return {
      ...state,
      customFilters: action.payload.customFilters,
      activeCustomFacets: removeUnselectedFiltersFromActiveFacetsState(
        // @ts-expect-error TODO(flow-migration)
        action.payload.customFilters,
        state.activeCustomFacets,
      ),
    };
  },

  [FACETING_UPDATE_FILTERS](state: ReduxState, action: SetFiltersAction): ReduxState {
    return {
      ...state,
      filters: action.payload.filters,
      activeFacets: removeUnselectedFiltersFromActiveFacetsState(
        // @ts-expect-error TODO(flow-migration)
        action.payload.filters,
        state.activeFacets,
      ),
    };
  },

  [FACETING_RESET_CUSTOM_FILTERS](state: ReduxState): ReduxState {
    return { ...state, activeCustomFacets: {}, customFilters: [] };
  },

  [FACETING_RESET_FILTERS](state: ReduxState): ReduxState {
    return {
      ...state,
      activeFacets: {},
      filters: [],
      query: {
        label: '',
      },
    };
  },
});
export {
  catalogToState,
  facetingReducer,
  filtersToActiveFacetsState,
  filtersToState,
  getTotalLabelsCount,
  initialState,
  PERTIMM_DEPARTURE_MAX_DATE,
  PERTIMM_DEPARTURE_MIN_DATE,
  removeUnselectedFiltersFromActiveFacetsState,
};
